import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { startCheckout } from "@/lib/payments";
import PaymentMethods from "@/components/PaymentMethods";
import { CheckCircle2, Loader2, XCircle, Rocket, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout | error
  const [info, setInfo] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "canceled", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep trying */ }
      if (attempts >= 10) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const retry = async () => {
    if (!info?.lookup_key || !info?.product_id) { navigate("/dashboard"); return; }
    setRetrying(true);
    try {
      await startCheckout(info.lookup_key, info.product_id);
    } catch (e) {
      toast.error("Kon de betaling niet opnieuw starten");
      setRetrying(false);
    }
  };

  const isBoost = info?.lookup_key && info.lookup_key.startsWith("boost");
  const failed = state === "failed" || state === "timeout" || state === "error";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950/80 p-8 text-center animate-fade-up">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-rose-400 animate-spin mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-zinc-100">Betaling controleren...</h1>
            <p className="text-sm text-zinc-500 mt-2">Een moment geduld.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-950/50 border border-emerald-800/50">
              {isBoost ? <Rocket className="h-7 w-7 text-emerald-400" /> : <CheckCircle2 className="h-7 w-7 text-emerald-400" />}
            </div>
            <h1 className="font-serif text-3xl text-zinc-100">{isBoost ? "Advertentie geboost!" : "Advertentie gepubliceerd!"}</h1>
            <p className="text-sm text-zinc-400 mt-3">
              {isBoost ? "Je advertentie staat nu vooraan in de lijst." : "Bedankt! Je advertentie staat nu live op Fetshop.nl."}
            </p>
            <div className="mt-6 flex flex-col gap-2">
              {info?.product_id && (
                <Link to={`/product/${info.product_id}`} data-testid="view-product-button" className="rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3 transition-all">
                  Bekijk advertentie
                </Link>
              )}
              <Link to="/dashboard" className="rounded-lg border border-zinc-800 text-zinc-300 px-5 py-3 hover:bg-zinc-900">Naar dashboard</Link>
            </div>
          </>
        )}
        {failed && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-rose-950/40 border border-rose-800/50">
              <XCircle className="h-8 w-8 text-rose-400" />
            </div>
            <h1 className="font-serif text-2xl text-zinc-100">Betaling niet voltooid</h1>
            <p className="text-sm text-zinc-400 mt-3">
              De betaling is geannuleerd of mislukt. Je advertentie is nog niet gepubliceerd — probeer het gerust opnieuw.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <button
                data-testid="retry-payment-button"
                onClick={retry}
                disabled={retrying || !info?.product_id}
                className="flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3 transition-all disabled:opacity-60"
              >
                <RotateCcw className={`h-4 w-4 ${retrying ? "animate-spin" : ""}`} /> {retrying ? "Opnieuw starten..." : "Probeer opnieuw"}
              </button>
              <button onClick={() => navigate("/dashboard")} className="rounded-lg border border-zinc-800 text-zinc-300 px-5 py-3 hover:bg-zinc-900">Naar dashboard</button>
            </div>
            <PaymentMethods className="mt-6" />
          </>
        )}
      </div>
    </div>
  );
}
